import { useState, useEffect, useMemo } from "react";
import { useProfile } from "@/hooks/useProfiles";
import AppBreadcrumbs from "@/components/AppBreadcrumbs";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { useRouteRestore } from "@/hooks/useRouteRestore";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, ChevronRight, ChevronDown, LogOut, Menu, Moon, Sun, Crown, HelpCircle } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useOrg } from "@/contexts/OrgContext";
import { useIsAdmin } from "@/hooks/useUserRole";
import { useActiveContext } from "@/hooks/useActiveContext";
import { useIsMobile } from "@/hooks/use-mobile";
import { useNavigationVisibility } from "@/hooks/useNavigationVisibility";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import NotificationBell from "@/components/NotificationBell";
import OrgOverrideBanner from "@/components/OrgOverrideBanner";
import MemberViewBanner from "@/components/MemberViewBanner";
import TrialDemoBanner from "@/components/trial/TrialDemoBanner";
import TrialExpiryBanner from "@/components/trial/TrialExpiryBanner";
import EmailVerificationBanner from "@/components/portal/EmailVerificationBanner";
import DemoEmailRoutingBanner from "@/components/trial/DemoEmailRoutingBanner";
import { useTerminology } from "@/hooks/useTerminology";
import OrgSwitcher from "@/components/OrgSwitcher";
import WorkspaceSwitcher from "@/components/workspaces/WorkspaceSwitcher";
import MemberOSLogoWhite from "@/assets/MemberOS-Logo-White.png";
import { useSidebarBadges } from "@/hooks/useSidebarBadges";
import { getRoleNav } from "@/config/roleNavConfigs";
import GlobalSearch from "@/components/GlobalSearch";
import WalkthroughOverlay from "@/components/walkthroughs/WalkthroughOverlay";
import HelpPanel from "@/components/walkthroughs/HelpPanel";
import { useWalkthrough } from "@/hooks/walkthroughs";
import { getNavItems, getMasterAdminNavItems, getAssociationAdminNavItems } from "./AppLayoutNavConfig";
import type { NavItem } from "./AppLayoutNavConfig";

const AppLayout = ({ children }: { children: React.ReactNode }) => {
  useRouteRestore();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({});
  const [darkMode, setDarkMode] = useState(() => {
    if (typeof window !== "undefined") return localStorage.getItem("theme") === "dark";
    return false;
  });
  const location = useLocation();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const { user, signOut } = useAuth();
  const { isAdmin, isMasterAdmin, isAssociationAdmin, role } = useIsAdmin();
  const { activeWorkspace } = useActiveContext();
  const { t: term } = useTerminology();
  const { overrideOrgName } = useOrg();
  const { data: badges } = useSidebarBadges();
  const { isModuleVisible } = useNavigationVisibility();
  const { data: userProfile } = useProfile(user?.id);
  const avatarUrl = userProfile?.avatar_url || null;
  const { toggleHelpPanel } = useWalkthrough();

  const isPlatformMode = (isMasterAdmin || isAssociationAdmin) && !overrideOrgName;

  const navItems = useMemo(() => {
    if (isPlatformMode) return isMasterAdmin ? getMasterAdminNavItems() : getAssociationAdminNavItems();
    if (isAdmin) return getNavItems(term("committees"));
    const roleNav = getRoleNav(activeWorkspace, term("committees"));
    if (roleNav) return roleNav as NavItem[];
    return getNavItems(term("committees"));
  }, [isPlatformMode, isMasterAdmin, isAdmin, activeWorkspace, term]);

  const displayName = user?.user_metadata?.display_name || user?.email?.split("@")[0] || "User";
  const initials = displayName
    .split(" ")
    .map((n: string) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);

  const roleBadge =
    isPlatformMode && isMasterAdmin
      ? "Platform Admin"
      : isPlatformMode && isAssociationAdmin
        ? "Association Admin"
        : isMasterAdmin && overrideOrgName
          ? `Viewing: ${overrideOrgName}`
          : isAssociationAdmin && overrideOrgName
            ? `Viewing: ${overrideOrgName}`
            : role === "admin"
              ? "Org Admin"
              : role === "moderator"
                ? "Moderator"
                : "Member";

  // biome-ignore lint/correctness/useExhaustiveDependencies: navItems is stable within a render
  useEffect(() => {
    const path = location.pathname;
    const newExpanded: Record<string, boolean> = {};

    navItems.forEach((item) => {
      if (item.children) {
        const parentBase = item.to.split("?")[0];
        const isActive = parentBase === "/" ? path === "/" : path === parentBase || path.startsWith(parentBase + "/");
        const childActive = item.children.some((c) => {
          const childBase = c.to.split("?")[0];
          return childBase !== "/" && path.startsWith(childBase);
        });
        if (isActive || childActive) newExpanded[item.label] = true;
      }
    });
    setExpandedGroups((prev) => ({ ...prev, ...newExpanded }));
  }, [location.pathname]);

  useEffect(() => {
    document.documentElement.classList.toggle("dark", darkMode);
    localStorage.setItem("theme", darkMode ? "dark" : "light");
  }, [darkMode]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: intentionally watching pathname only
  useEffect(() => setMobileOpen(false), [location.pathname]);

  const toggleGroup = (label: string) => setExpandedGroups((prev) => ({ ...prev, [label]: !prev[label] }));

  const showExpanded = isMobile || !collapsed;

  const renderNavItem = (item: NavItem) => {
    const basePath = item.to.split("?")[0];
    const hasChildren = item.children && item.children.length > 0;
    const childActive =
      hasChildren &&
      item.children!.some((c) => location.pathname.startsWith(c.to.split("?")[0]) && c.to.split("?")[0] !== "/");
    const isActive =
      basePath === "/"
        ? location.pathname === "/"
        : hasChildren
          ? location.pathname.startsWith(basePath) || childActive
          : location.pathname === basePath;
    const isExpanded = expandedGroups[item.label];
    const isWorkspaceEmphasis = !isPlatformMode && !!activeWorkspace && item.workspace === activeWorkspace;

    const visibleChildren = item.children?.filter((child) => {
      if (child.to === "/admin" && !isAdmin) return false;
      if (!isPlatformMode && child.module && !isModuleVisible(child.module)) return false;
      return true;
    });

    if (!hasChildren) {
      return (
        <NavLink
          key={item.to}
          to={item.to}
          className={`flex items-center gap-2.5 px-2.5 py-2 rounded-md text-[13px] transition-all relative group ${
            isActive
              ? "bg-primary/10 text-primary font-medium"
              : isWorkspaceEmphasis
                ? "text-sidebar-foreground font-medium hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
                : "text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
          }`}
        >
          {isActive && (
            <motion.div
              layoutId="nav-active"
              className="absolute left-0 top-1/2 -translate-y-1/2 w-[2px] h-4 rounded-full bg-primary"
              transition={{ type: "spring", stiffness: 400, damping: 30 }}
            />
          )}
          <item.icon className="w-4 h-4 shrink-0" />
          {showExpanded && <span className="flex-1 whitespace-nowrap overflow-hidden">{item.label}</span>}
          {isWorkspaceEmphasis && !isActive && showExpanded && (
            <span className="w-1.5 h-1.5 rounded-full bg-primary/40 shrink-0" />
          )}
          {badges?.[item.to] && badges[item.to] > 0 && (
            <span className="ml-auto min-w-[18px] h-[18px] flex items-center justify-center rounded-full bg-destructive text-destructive-foreground text-[10px] font-bold px-1">
              {badges[item.to] > 99 ? "99+" : badges[item.to]}
            </span>
          )}
        </NavLink>
      );
    }

    return (
      <div key={item.label} className="space-y-0.5">
        <button
          onClick={() => (showExpanded ? toggleGroup(item.label) : navigate(item.to))}
          type="button"
          className={`flex items-center gap-2.5 w-full px-2.5 py-2 rounded-md text-[13px] transition-all relative group ${
            isActive
              ? "bg-primary/10 text-primary font-medium"
              : isWorkspaceEmphasis
                ? "text-sidebar-foreground font-medium hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
                : "text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
          }`}
        >
          {isActive && (
            <motion.div
              layoutId="nav-active"
              className="absolute left-0 top-1/2 -translate-y-1/2 w-[2px] h-4 rounded-full bg-primary"
              transition={{ type: "spring", stiffness: 400, damping: 30 }}
            />
          )}
          <item.icon className="w-4 h-4 shrink-0" />
          {showExpanded && (
            <>
              <span className="flex-1 text-left whitespace-nowrap overflow-hidden">{item.label}</span>
              {(() => {
                const groupTotal = visibleChildren?.reduce((sum, c) => sum + (badges?.[c.to] || 0), 0) || 0;
                return !isExpanded && groupTotal > 0 ? (
                  <span className="min-w-[18px] h-[18px] flex items-center justify-center rounded-full bg-destructive text-destructive-foreground text-[10px] font-bold px-1 mr-1">
                    {groupTotal > 99 ? "99+" : groupTotal}
                  </span>
                ) : null;
              })()}
              <ChevronDown
                className={`w-3 h-3 shrink-0 text-muted-foreground transition-transform duration-200 ${isExpanded ? "rotate-180" : ""}`}
              />
            </>
          )}
        </button>

        {showExpanded && (
          <AnimatePresence initial={false}>
            {isExpanded && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.2, ease: "easeInOut" }}
                className="overflow-hidden"
              >
                <div className="ml-4 pl-2.5 border-l border-border/50 space-y-0.5 py-0.5">
                  {visibleChildren?.map((child) => {
                    const childBasePath = child.to.split("?")[0];
                    const childActiveState =
                      location.pathname === childBasePath &&
                      (child.to.includes("?") ? location.search.includes(child.to.split("?")[1]) : !location.search);
                    return (
                      <NavLink
                        key={child.to}
                        to={child.to}
                        className={`flex items-center gap-2 px-2 py-1.5 rounded-md text-[12px] transition-all ${childActiveState ? "text-primary font-medium bg-primary/5" : "text-muted-foreground hover:text-foreground hover:bg-sidebar-accent"}`}
                      >
                        <child.icon className="w-3.5 h-3.5 shrink-0" />
                        <span className="flex-1 whitespace-nowrap overflow-hidden">{child.label}</span>
                        {badges?.[child.to] && badges[child.to] > 0 && (
                          <span className="ml-auto min-w-[16px] h-[16px] flex items-center justify-center rounded-full bg-destructive text-destructive-foreground text-[9px] font-bold px-0.5">
                            {badges[child.to] > 99 ? "99+" : badges[child.to]}
                          </span>
                        )}
                      </NavLink>
                    );
                  })}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        )}
      </div>
    );
  };

  const sidebarContent = (
    <div className="flex flex-col h-full min-h-0">
      <div className="flex items-center px-4 h-14 border-b border-border shrink-0">
        {showExpanded ? (
          <img src={MemberOSLogoWhite} alt="MemberOS" className="h-11" />
        ) : (
          <img
            src={MemberOSLogoWhite}
            alt="MemberOS"
            className="h-11 object-contain object-left"
            style={{ maxWidth: "28px", overflow: "hidden" }}
          />
        )}
      </div>

      <nav className="flex-1 py-3 px-2 space-y-0.5 overflow-y-auto">
        {navItems
          .filter((item) => {
            if (item.adminOnly && !isAdmin) return false;
            if (!isPlatformMode && item.module && !isModuleVisible(item.module)) return false;
            return true;
          })
          .map(renderNavItem)}
      </nav>

      <div className="border-t border-border shrink-0">
        <button
          onClick={() => navigate(isMasterAdmin ? "/master-admin" : "/profile")}
          type="button"
          className="flex items-center gap-2.5 w-full px-3 py-3 hover:bg-sidebar-accent transition-colors text-left"
        >
          <div className="relative shrink-0">
            {avatarUrl ? (
              <img src={avatarUrl} alt={displayName} className="w-8 h-8 rounded-full object-cover" />
            ) : (
              <div className="w-8 h-8 rounded-full bg-primary/15 flex items-center justify-center text-[11px] font-semibold text-primary">
                {initials}
              </div>
            )}
            {isMasterAdmin && (
              <div className="absolute -top-1 -right-1 w-3.5 h-3.5 rounded-full bg-warning flex items-center justify-center">
                <Crown className="w-2 h-2 text-warning-foreground" />
              </div>
            )}
          </div>
          {showExpanded && (
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium text-sidebar-accent-foreground truncate">{displayName}</div>
              <div className="text-xs text-muted-foreground truncate flex items-center gap-1">
                {isMasterAdmin && <Crown className="w-2.5 h-2.5 text-warning" />}
                {roleBadge}
              </div>
            </div>
          )}
        </button>

        <div className="px-2 pb-2 space-y-0.5">
          <button
            onClick={signOut}
            type="button"
            className="flex items-center justify-center w-full gap-2 px-2.5 py-2 rounded-md text-[13px] text-destructive hover:bg-destructive/10 transition-all"
          >
            <LogOut className="w-4 h-4" />
            {showExpanded && <span>Sign Out</span>}
          </button>
          {!isMobile && (
            <button
              onClick={() => setCollapsed(!collapsed)}
              type="button"
              className="flex items-center justify-center w-full gap-2 px-2.5 py-2 rounded-md text-[13px] text-sidebar-foreground hover:bg-sidebar-accent transition-all"
            >
              {collapsed ? (
                <ChevronRight className="w-4 h-4" />
              ) : (
                <>
                  <ChevronLeft className="w-4 h-4" />
                  <span>Collapse</span>
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <div className="flex h-screen overflow-hidden bg-background">
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:z-[100] focus:top-2 focus:left-2 focus:px-4 focus:py-2 focus:bg-primary focus:text-primary-foreground focus:rounded-md focus:text-sm focus:font-medium"
      >
        Skip to main content
      </a>
      {!isMobile && (
        <motion.aside
          animate={{ width: collapsed ? 64 : 240 }}
          transition={{ duration: 0.2, ease: "easeInOut" }}
          className="relative flex flex-col h-full border-r border-border bg-sidebar shrink-0 z-20 overflow-hidden"
        >
          {sidebarContent}
        </motion.aside>
      )}
      {isMobile && (
        <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
          <SheetContent side="left" className="w-[280px] p-0 flex flex-col">
            <SheetTitle className="sr-only">Navigation</SheetTitle>
            {sidebarContent}
          </SheetContent>
        </Sheet>
      )}

      <div className="flex-1 flex flex-col overflow-hidden">
        <header className="h-14 border-b border-border bg-background/80 backdrop-blur-md flex items-center justify-between px-4 md:px-6 shrink-0 z-10">
          <div className="flex items-center gap-3">
            {isMobile && (
              <button
                onClick={() => setMobileOpen(true)}
                type="button"
                className="p-1.5 rounded-md hover:bg-secondary transition-colors"
              >
                <Menu className="w-5 h-5 text-foreground" />
              </button>
            )}
            <GlobalSearch />
          </div>
          <TooltipProvider delayDuration={300}>
            <div className="flex items-center gap-2">
              <WorkspaceSwitcher />
              <OrgSwitcher />
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    onClick={() => setDarkMode(!darkMode)}
                    className="p-2 rounded-md hover:bg-secondary transition-colors"
                    aria-label="Toggle dark mode"
                    type="button"
                  >
                    {darkMode ? (
                      <Sun className="w-4 h-4 text-muted-foreground" />
                    ) : (
                      <Moon className="w-4 h-4 text-muted-foreground" />
                    )}
                  </button>
                </TooltipTrigger>
                <TooltipContent side="bottom">
                  <p>{darkMode ? "Switch to light mode" : "Switch to dark mode"}</p>
                </TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    onClick={toggleHelpPanel}
                    className="p-2 rounded-md hover:bg-secondary transition-colors"
                    aria-label="Help & guides"
                    type="button"
                  >
                    <HelpCircle className="w-4 h-4 text-muted-foreground" />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="bottom">
                  <p>Help & guides</p>
                </TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span>
                    <NotificationBell />
                  </span>
                </TooltipTrigger>
                <TooltipContent side="bottom">
                  <p>Notifications</p>
                </TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger asChild>
                  {avatarUrl ? (
                    <button
                      onClick={() => navigate("/profile")}
                      className="w-7 h-7 rounded-full overflow-hidden cursor-pointer hover:ring-2 hover:ring-primary/30 transition-all"
                      type="button"
                      aria-label="Your profile"
                    >
                      <img src={avatarUrl} alt={displayName} className="w-full h-full object-cover" />
                    </button>
                  ) : (
                    <button
                      onClick={() => navigate("/profile")}
                      className="w-7 h-7 rounded-full bg-primary/15 flex items-center justify-center text-[10px] font-semibold text-primary hover:bg-primary/25 transition-colors cursor-pointer"
                      type="button"
                    >
                      {initials}
                    </button>
                  )}
                </TooltipTrigger>
                <TooltipContent side="bottom">
                  <p>Your profile</p>
                </TooltipContent>
              </Tooltip>
            </div>
          </TooltipProvider>
        </header>

        <OrgOverrideBanner />
        <MemberViewBanner />
        <TrialExpiryBanner />
        <TrialDemoBanner />
        <EmailVerificationBanner />
        <DemoEmailRoutingBanner />

        <main id="main-content" className="flex-1 overflow-y-auto">
          <div className="min-h-full">
            <div className="px-6 lg:px-8 pt-4">
              <AppBreadcrumbs />
            </div>
            {children}
          </div>
        </main>
      </div>
      <WalkthroughOverlay />
      <HelpPanel />
    </div>
  );
};

export default AppLayout;
